import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';

interface SidebarRowProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  open: boolean;
  onToggle: () => void;
  disabled?: boolean;
  disabledLabel?: string;
  children?: React.ReactNode;
  badge?: React.ReactNode;
}

/**
 * Single-line collapsed sidebar row (Contact info / Linked Tickets / Parent
 * Child / Atlassian Jira Plus in the reference screenshots): icon + label on
 * the left, chevron on the right, expands in place when clicked. A disabled
 * row (integration not wired up yet) renders the same shell without an
 * expand affordance so it reads as "coming soon" rather than broken.
 */
export function SidebarRow({ icon: Icon, label, open, onToggle, disabled, disabledLabel, children, badge }: SidebarRowProps) {
  return (
    <div className="border-b border-line last:border-b-0">
      <button
        type="button"
        onClick={disabled ? undefined : onToggle}
        disabled={disabled}
        aria-expanded={disabled ? undefined : open}
        className={cn(
          'flex w-full items-center gap-2.5 px-4 py-3 text-left text-sm font-medium text-ink-muted',
          disabled ? 'cursor-default opacity-60' : 'hover:bg-surface-hover',
        )}
      >
        <Icon className="h-4 w-4 shrink-0 text-ink-subtle" />
        <span className="flex-1 truncate">{label}</span>
        {badge}
        {disabled ? (
          <span className="text-xs text-ink-subtle">{disabledLabel}</span>
        ) : (
          <ChevronDown
            className={cn('h-4 w-4 shrink-0 text-ink-subtle transition-transform', open && 'rotate-180')}
          />
        )}
      </button>
      {!disabled && open && (
        <div className="border-t border-line bg-surface-hover/40 px-4 py-3 text-sm">
          {children}
        </div>
      )}
    </div>
  );
}
